import React from 'react';
import { View, ActivityIndicator, Text, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';

interface LoadingScreenProps {
  message?: string;
  size?: 'small' | 'large';
}

/**
 * ✅ P1-03: Standardized Loading Screen Component
 *
 * Used when:
 * - Initial data fetch is in progress
 * - Screen is loading for the first time
 * - Long-running operations
 *
 * Features:
 * - RTL support (Arabic/English)
 * - Dark mode support
 * - Customizable message
 * - Accessible (screen reader friendly)
 */
export const LoadingScreen: React.FC<LoadingScreenProps> = ({ message, size = 'large' }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: theme.colors.background,
      padding: 20,
    },
    text: {
      marginTop: 16,
      fontSize: 16,
      color: theme.colors.textSecondary,
      textAlign: 'center',
    },
  });

  return (
    <View style={styles.container}>
      <ActivityIndicator
        size={size}
        color={theme.colors.primary}
        accessibilityLabel={t('loading')}
      />
      {message && <Text style={styles.text}>{message}</Text>}
    </View>
  );
};

export default LoadingScreen;
